import type { FieldSchema, Filter, Operator } from '@/stores'
import { formatCalendarDate } from '@/utils'
import { getDefaultOperators } from './constants'

export function formatFilterLabel(
  filter: Filter,
  schema: FieldSchema[],
): string {
  const fieldSchema = schema.find((s) => s.field === filter.field)
  if (!fieldSchema) return ''

  const { label, type, operators: customOperators, formatter } = fieldSchema
  const operators: Operator[] = customOperators
    ? customOperators.map((op) =>
        typeof op === 'string' ? { value: op, label: op } : op,
      )
    : getDefaultOperators(type)

  const getOperatorLabel = (op: string): string => {
    const operator = operators.find((o) => o.value === op)
    return operator?.label || op
  }

  if (type === 'select') {
    if (Array.isArray(filter.value)) {
      const count = filter.value.length
      if (count === 1) {
        const displayValue = formatter
          ? formatter(filter.value[0])
          : filter.value[0]
        return `${label}: ${displayValue}`
      }
      return `${label}: ${count} selected`
    }
    const displayValue = formatter ? formatter(filter.value) : filter.value
    return `${label}: ${displayValue}`
  }

  if (type === 'number') {
    const operatorLabel = getOperatorLabel(filter.operator)
    if (filter.operator === 'between') {
      const val1 = formatter ? formatter(filter.value) : filter.value
      const val2 = formatter ? formatter(filter.value2) : filter.value2
      return `${label}: ${operatorLabel} ${val1} - ${val2}`
    }
    const displayValue = formatter ? formatter(filter.value) : filter.value
    return `${label}: ${operatorLabel} ${displayValue}`
  }

  if (type === 'date') {
    const operatorLabel = getOperatorLabel(filter.operator)
    if (filter.operator === 'between') {
      const date1 = formatCalendarDate(filter.value)
      const date2 = formatCalendarDate(filter.value2)
      return `${label}: ${operatorLabel} ${date1} - ${date2}`
    }
    const dateStr = formatCalendarDate(filter.value)
    return `${label}: ${operatorLabel} ${dateStr}`
  }

  if (type === 'text') {
    const operatorLabel = getOperatorLabel(filter.operator)
    return `${label}: ${operatorLabel} "${filter.value}"`
  }

  if (type === 'boolean') {
    return label
  }

  return `${label}: ${filter.value}`
}
